import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { reportMBSPFilterOptions } from "../report-mbsp";
import { ReportMBSPFilterSettingsForm } from "../settings-form/ReportMBSPFilterSettingsForm";
import { ReportMBSPFilterSettings } from "../settings/ReportMBSPFilterSettings";
import { ReportMBSPRow } from "../table-data/ReportMBSPRow";
import { ReportMBSPJournalRequest } from "../journal/ReportMBSPJournalRequest";
import { Option } from "@/modules/budget/monitoring/reports-constructor/common/types/Option";
import { ReportMBSPTableDataRequest } from "../data-request/ReportMBSPTableDataRequest";

export class ReportMBSPFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportMBSPFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'MBSP') {
            throw new Error('не соответсвующий подтип FilterSettings')
        }
        if (options.key !== 'MBSP') {
            throw new Error('не соответсвующий подтип FilterOptions')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('Не найден reportName')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('Не найден reportType')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('Не найден classificationType')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('Не найден dataSource')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('Не найден periodicity')
        }
        periodicity = { ...periodicity }

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('Не найден measureUnitOption')
        }
        measureUnitOption = { ...measureUnitOption }


        const budgetTypeOptions = cloneDeep(
            options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))
        )

        const statusOptions = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        const mutuallyRedeemingLevelOptions = cloneDeep(
            options.mutuallyRedeemingLevels.filter(it => settings.mutuallyRedeemingLevels.includes(it.value))
        )

        const regions: Option<string>[] = []
        settings.regions.forEach(region => {
            const option = options.regions.find(it => it.value === region)!
            regions.push({...option})
        })

        const byFormOptions = options.byForms.filter(it => settings.byForms.includes(it.value))
        if (!byFormOptions) {
            throw new Error('Не найден byFormOption')
        }

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }


        // 2. Формирование
        const form: ReportMBSPFilterSettingsForm = {
            // Base:
            key: 'MBSP',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            date: new Date(settings.date),
            measureUnit: measureUnitOption,
            budgetTypes: budgetTypeOptions,
            statuses: statusOptions,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            byForms: byFormOptions,
            regions: regions,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo,
            highLevelExpenses: settings.highLevelExpenses,
            planByMonths: settings.planByMonths,
            incomes: cloneDeep(settings.incomes),
            expenses: cloneDeep(settings.expenses),
            indicators: cloneDeep(settings.indicators)
        };

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'MBSP') {
            throw new Error('не соответсвующий подтип FilterSettingsForm')
        }

        if (!form.date) {
            throw new Error('Необходимо ввести "Дата"')
        }

        if (!form.measureUnit) {
            throw new Error('Необходимо ввести "Единица измерения"')
        }

        if (!form.regions) {
            throw new Error('Необходимо ввести "Регион"')
        }

        if (!form.byForms) {
            throw new Error('Необходимо ввести "По форме"')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')
        const settings: ReportMBSPFilterSettings = {
            // Base:
            reportName: 'MBSP',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            date: strDate,
            budgetTypes: form.budgetTypes.map(it => it.value),
            regions: form.regions.map(it => it.value),
            statuses: form.statuses.map(it => it.value),
            byForms: form.byForms.map(it => it.value),
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            highLevelExpenses: form.highLevelExpenses,
            planByMonths: form.planByMonths,
            incomes: cloneDeep(form.incomes),
            expenses: cloneDeep(form.expenses),
            indicators: cloneDeep(form.indicators)
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportMBSPFilterOptions)

        const yesterdayMoment = moment().subtract(1, 'days')
        const yesterdayDate = yesterdayMoment.toDate()
        const emptyForm: ReportMBSPFilterSettingsForm = {
            key: 'MBSP',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'MBSP')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'MIXED')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            // Custom fields
            date: yesterdayDate,
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            budgetTypes: [],
            regions: [],
            mutuallyRedeemingLevels: options.mutuallyRedeemingLevels,
            statuses: options.statuses.filter(it => it.value === '39'),
            byForms: options.byForms.filter(it => ['2-19', '4-20'].includes(it.value))!,
            roundUp: false,
            highLevelExpenses: false,
            planByMonths: false,
            roundUpTo: null,
            incomes: cloneDeep(options.incomes),
            expenses: cloneDeep(options.expenses),
            indicators: cloneDeep(options.indicators.slice(0, 5))
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'MBSP') {
            throw new Error('не соответсвующий подтип FilterSettingsForm')
        }

        if (!form.date) {
            throw new Error('Необходимо ввести "Дата"')
        }
        if (form.budgetTypes.length === 0) {
            throw new Error('Необходимо ввести "Бюджет"')
        }
        if (!form.measureUnit) {
            throw new Error('Необходимо ввести "Единица измерения"')
        }
        if (!form.regions) {
            throw new Error('Необходимо ввести "Регион"')
        }


        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const vzkList = form.mutuallyRedeemingLevels.map(it => {
            if (it.value === 'TYPE-3') {
                return 3
            } else if (it.value === 'TYPE-4') {
                return 4
            } else {
                throw new Error('Неподдерживаемый уровень ВЗК')
            }
        })


        let source: 'integration' | 'loader' = 'integration'
        if (form.dataSource.value === 'IISK') {
            source = 'integration'
        } else if (form.dataSource.value === 'LOADER') {
            source = 'loader'
        } else {
            throw new Error('Неподдерживаемый источник данных')
        }

        const params: ReportMBSPTableDataRequest = {
            user_id: userId,
            incomes: form.incomes,
            expenses: form.expenses,
            source: source,
            repdate: strDate,
            regions: form.regions.map(r => r.value),
            vzk_list: vzkList,
            budget_types: form.budgetTypes.map(it => it.value),
            status_list: form.statuses.map(it => it.value),
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo,
            high_level_expenses: form.highLevelExpenses,
            plan_by_months: form.planByMonths,
            locale: i18nService.locale
        }

        const indicatorsOrder: string[] = form.indicators.filter(it => it.active).map(it => it.key)

        const response = await fetch(`/api-py/monitoring/reports-constructor/MBSP`,
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('Не удалось получить данные для отчета')
        }
        const responseTableData: any[] = await response.json()
        const tableData: ReportMBSPRow[] = responseTableData.map((row: any, index) => {
            row.index = index
            return row
        })

        // 2. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: ReportMBSPJournalRequest = {
            reportName: "MBSP",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            indicators_order: indicatorsOrder, // Может быть редактирована во время превью
            params: params,
            locale: i18nService.locale,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
